import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInterview } from '@features/interview/context/InterviewContext';
import { Button, Input } from '@shared/components/ui/UI';
import { UserIcon, LockIcon, AlertIcon, EyeIcon, EyeOffIcon } from '@shared/components/icons';
import apiService from '@shared/services/api.service';
import { isValidEmail } from '@shared/utils/helpers';
import { ROUTES } from '@shared/utils/constants';
import tokenStorage from '@features/auth/utils/tokenStorage';

// Assets
import loginBackground from '@assets/images/Background.jpg';
import IqarxLogo from '@assets/svg/iqarx.svg';
import QiLogo from '@assets/svg/Qi.svg';

const LoginPage = () => {
  const navigate = useNavigate();
  const { actions } = useInterview();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'البريد الإلكتروني غير صالح';
    }

    if (!password.trim()) {
      newErrors.password = 'كلمة المرور مطلوبة';
    } else if (password.length < 6) {
      newErrors.password = 'كلمة المرور قصيرة جداً';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const response = await apiService.login(email, password);

      // API returns: { message, encrypted_token, access_token }
      if (response.access_token) {
        // Store tokens securely (access_token in memory, encrypted_token in sessionStorage)
        tokenStorage.setTokens(response.access_token, response.encrypted_token);

        // Store session data
        const sessionData = {
          user: {
            email,
            name: email.split('@')[0], // Extract name from email temporarily
          },
          sessionId: Date.now().toString(), // Generate temp session ID
        };

        tokenStorage.setSessionData(sessionData);

        // Update context state
        actions.login({
          ...sessionData,
          accessToken: response.access_token,
          encryptedToken: response.encrypted_token,
        });

        actions.setInterviewData({
          interviewType: 'Technical Interview',
          interviewer: {
            name: 'Sarah',
            role: 'AI Interviewer',
          },
        });

        actions.setPhase('proceed');
        navigate(ROUTES.PROCEED);
      }
    } catch (error) {
      console.error('Login error:', error);

      let errorMessage = 'البريد الإلكتروني أو كلمة المرور غير صحيحة';

      // Priority order for error messages:
      // 1. Check for "User not found" in detail field
      // 2. Check status codes first (400, 401, 403, etc.)
      // 3. API error message from data object (only if meaningful)
      // 4. API error property from data object
      // 5. Generic fallback

      if (error.data?.detail === "User not found") {
        // Show specific message for user not found
        errorMessage = 'المستخدم غير موجود';
      } else if (error.status === 400 || error.status === 401) {
        errorMessage = 'البريد الإلكتروني أو كلمة المرور غير صحيحة';
      } else if (error.status === 403) {
        errorMessage = 'تم رفض الوصول. يرجى التواصل مع الدعم';
      } else if (error.status === 404) {
        errorMessage = 'نقطة النهاية غير موجودة. يرجى التحقق من التكوين';
      } else if (error.status === 500) {
        errorMessage = 'خطأ في الخادم. يرجى المحاولة لاحقاً';
      } else if (error.status === 0 || error.isNetworkError) {
        errorMessage = 'فشل الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت';
      } else if (error.data?.message && !error.data.message.includes('خطأ في الاتصال')) {
        // Show the exact message from API (only if it's not a generic connection error message)
        errorMessage = error.data.message;
      } else if (error.data?.error && !error.data.error.includes('خطأ في الاتصال')) {
        // Show the error field from API
        errorMessage = error.data.error;
      }

      console.log('Login error details:', {
        status: error.status,
        message: error.message,
        data: error.data,
        detail: error.data?.detail,
        displayedMessage: errorMessage,
      });

      setErrors({
        general: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-white">
      {/* Background Image - Left Side Only (Hidden on Mobile) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="hidden sm:block absolute left-0 top-0 bottom-0 sm:right-[70%] md:right-[55%] lg:right-[45%] xl:right-[40%]"
      >
        <img
          src={loginBackground}
          alt="Login Background"
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* IQARX Logo - Top Left (fixed position) */}
      {/* <div className="absolute top-8 left-8 z-10">
        <img src={IqarxLogo} alt="IQARX" className="h-10 w-auto" />
      </div> */}

      {/* QiCard Logo - Bottom Left (fixed position) */}
      {/* <div className="absolute bottom-8 left-8 z-10">
        <img src={QiLogo} alt="Qi Card" className="h-12 w-auto opacity-60" />
      </div> */}

      {/* Login Form - Full width on mobile, partial width on larger screens */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="absolute right-0 top-0 bottom-0 w-full sm:w-[70%] md:w-[55%] lg:w-[45%] xl:w-[40%] flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-white/95 backdrop-blur-sm z-20"
      >
        <div className="w-full max-w-md" dir="rtl">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {/* Logos */}
            <div className="flex items-center justify-center gap-12 mb-6">
              <img
                src={QiLogo}
                alt="Qi Card"
                className="h-10 w-auto"
                style={{ filter: 'brightness(0)' }}
              />
              <img
                src={IqarxLogo}
                alt="IQARX"
                className="h-10 w-auto"
                style={{ filter: 'brightness(0)' }}
              />
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-gray-900 mb-3 font-arabic">
              تسجيل الدخول إلى جلستك
            </h1>

            {/* Subtitle */}
            <p className="text-sm mb-6 font-arabic" style={{ color: '#62748E' }}>
              الرجاء إدخال بيانات الاعتماد المتوفرة في رسالة الدعوة الإلكترونية.
            </p>

            {/* Error message */}
            {errors.general && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3"
              >
                <AlertIcon className="text-red-500 flex-shrink-0" size={20} />
                <span className="text-red-700 text-sm">{errors.general}</span>
              </motion.div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="البريد الإلكتروني"
                type="email"
                icon={UserIcon}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                autoComplete="email"
                labelStyle={{ color: '#314158' }}
                inputClassName="!bg-blue-50"
                dir="ltr"
                style={{ textAlign: 'left' }}
              />

              <div className="relative">
                <Input
                  label="كلمة المرور"
                  type={showPassword ? "text" : "password"}
                  icon={LockIcon}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={errors.password}
                  autoComplete="current-password"
                  labelStyle={{ color: '#314158' }}
                  inputClassName="!bg-blue-50 !pe-12"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-[38px] text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
                </button>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 rounded-xl"
                loading={isLoading}
              >
                ادخل
              </Button>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;